import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import {
  hubDataTableColumnLayoutStateKey,
  mergeColumnIdsWithLayout,
  parseHubDataTableColumnLayoutSnapshot,
  serializeHubDataTableColumnLayoutSnapshot,
  type HubDataTableColumnLayoutSnapshot,
} from "@/lib/hubDataTableColumnLayout";

function hubUiStateQueryKey(stateKey: string): readonly ["hub-ui-state", string] {
  return ["hub-ui-state", stateKey];
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

export function useHubDataTableColumnLayout(
  tableId: string,
  allColumnIds: readonly string[],
  enabled: boolean,
): {
  layoutReady: boolean;
  columnOrder: string[];
  hiddenColumnIds: string[];
  setColumnOrder: (next: string[] | ((current: string[]) => string[])) => void;
  setHiddenColumnIds: (next: string[] | ((current: string[]) => string[])) => void;
  resetLayout: () => void;
} {
  const trimmedTableId = tableId.trim();
  const layoutEnabled = enabled && trimmedTableId !== "";
  const stateKey = layoutEnabled ? hubDataTableColumnLayoutStateKey(trimmedTableId) : "";
  const queryClient = useQueryClient();

  const [columnOrder, setColumnOrder] = useState<string[]>([...allColumnIds]);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<string[]>([]);
  const [layoutReady, setLayoutReady] = useState(!layoutEnabled);
  const skipNextPersistRef = useRef(false);

  const layoutQuery = useQuery({
    queryKey: hubUiStateQueryKey(stateKey),
    enabled: layoutEnabled,
    queryFn: async () => {
      const response = await apiFetch<{ value: unknown }>(
        `/api/me/ui-state/${encodeURIComponent(stateKey)}`,
      );
      return parseHubDataTableColumnLayoutSnapshot(response.value);
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!layoutEnabled) {
      setLayoutReady(true);
      return;
    }
    // Dynamic tables (wireless reports) start with no column ids until the API responds.
    // Persisting that empty list would overwrite the saved profile before real columns exist.
    if (allColumnIds.length === 0 || layoutQuery.isPending) {
      setLayoutReady(false);
      return;
    }
    if (layoutQuery.isError) {
      skipNextPersistRef.current = true;
      setColumnOrder([...allColumnIds]);
      setHiddenColumnIds([]);
      setLayoutReady(true);
      return;
    }
    const merged = mergeColumnIdsWithLayout(allColumnIds, layoutQuery.data ?? null);
    const nextHidden = [...merged.hiddenIds];
    setColumnOrder((current) => {
      if (sameIds(current, merged.orderedIds)) return current;
      skipNextPersistRef.current = true;
      return merged.orderedIds;
    });
    setHiddenColumnIds((current) => {
      if (sameIds(current, nextHidden)) return current;
      skipNextPersistRef.current = true;
      return nextHidden;
    });
    setLayoutReady(true);
  }, [allColumnIds, layoutEnabled, layoutQuery.data, layoutQuery.isError, layoutQuery.isPending]);

  const persistMutation = useMutation({
    mutationFn: async (snapshot: HubDataTableColumnLayoutSnapshot) => {
      await apiFetch(`/api/me/ui-state/${encodeURIComponent(stateKey)}`, {
        method: "PUT",
        // apiFetch stringifies `body`. A pre-encoded string is stored as JSON text and the route rejects it.
        body: { value: serializeHubDataTableColumnLayoutSnapshot(snapshot) },
      });
    },
    onSuccess: (_data, snapshot) => {
      queryClient.setQueryData(hubUiStateQueryKey(stateKey), snapshot);
    },
  });

  const persistMutateRef = useRef(persistMutation.mutate);
  persistMutateRef.current = persistMutation.mutate;
  const lastPersistedRef = useRef("");

  useEffect(() => {
    if (!layoutEnabled || !layoutReady) {
      return;
    }
    const serialized = JSON.stringify({ columnOrder, hiddenColumnIds });
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      lastPersistedRef.current = serialized;
      return;
    }
    if (serialized === lastPersistedRef.current) {
      return;
    }
    lastPersistedRef.current = serialized;
    persistMutateRef.current({ columnOrder, hiddenColumnIds });
  }, [columnOrder, hiddenColumnIds, layoutEnabled, layoutReady]);

  const resetLayout = useCallback(() => {
    setColumnOrder([...allColumnIds]);
    setHiddenColumnIds([]);
    if (layoutEnabled) {
      void apiFetch(`/api/me/ui-state/${encodeURIComponent(stateKey)}`, { method: "DELETE" });
      queryClient.setQueryData(hubUiStateQueryKey(stateKey), null);
    }
  }, [allColumnIds, layoutEnabled, queryClient, stateKey]);

  return useMemo(
    () => ({
      layoutReady,
      columnOrder,
      hiddenColumnIds,
      setColumnOrder,
      setHiddenColumnIds,
      resetLayout,
    }),
    [columnOrder, hiddenColumnIds, layoutReady, resetLayout],
  );
}
